import React from 'react';
import { BarChart3 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ItemConsumptionDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useChartColors } from '../useChartColors';
import { formatDate, formatQty } from '../format';
import { SectionCard } from './SectionCard';

interface ItemConsumptionSectionProps {
  consumption: ItemConsumptionDto;
}

const weekLabel = (d: string) =>
  new Date(d).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="text-xs text-gray-500 mb-0.5">{label}</div>
    <div className="text-base font-semibold text-gray-900">{value}</div>
  </div>
);

/**
 * Lagersjef: forbruk = plukket antall på ferdige plukklister (ikke StcTransaction —
 * se ItemOverviewRepository). Ukesgraf siste 13 uker, én serie uten legend.
 */
export const ItemConsumptionSection: React.FC<ItemConsumptionSectionProps> = ({ consumption }) => {
  const { t } = useTranslation();
  const colors = useChartColors();
  const k = (key: string, fallback: string, params: Record<string, string> = {}) =>
    t(`warehouse.modules.itemCard.consumption.${key}`, params, fallback);

  const hasPicks = consumption.weeklyPicked.some(w => w.quantity > 0);

  return (
    <SectionCard icon={<BarChart3 className="h-4 w-4" />} title={k('title', 'Forbruk')}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        <Stat label={k('last30', 'Plukket siste 30 dager')} value={formatQty(consumption.pickedLast30Days)} />
        <Stat label={k('last90', 'Plukket siste 90 dager')} value={formatQty(consumption.pickedLast90Days)} />
        <Stat
          label={k('daysOfCover', 'Lageret rekker')}
          value={consumption.daysOfCover != null
            ? k('days', `${formatQty(consumption.daysOfCover)} dager`, { '0': formatQty(consumption.daysOfCover) })
            : '-'}
        />
        <Stat label={k('lastPick', 'Sist plukket')} value={formatDate(consumption.lastPickDate)} />
        <Stat label={k('lastReceipt', 'Sist mottatt')} value={formatDate(consumption.lastReceiptDate)} />
        <Stat label={k('lastCount', 'Sist talt')} value={formatDate(consumption.lastCountDate)} />
      </div>

      {hasPicks ? (
        <div className="h-48 sm:h-56" role="img" aria-label={k('weeklyTitle', 'Plukket per uke')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={consumption.weeklyPicked} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap={2}>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="weekStart" tickFormatter={weekLabel} tick={{ fill: colors.axisText, fontSize: 11 }} stroke={colors.grid} interval="preserveStartEnd" />
              <YAxis tickFormatter={(v: number) => formatQty(v)} tick={{ fill: colors.axisText, fontSize: 11 }} stroke={colors.grid} width={56} />
              <Tooltip
                cursor={{ fill: colors.grid, opacity: 0.4 }}
                contentStyle={{ background: colors.surface, borderColor: colors.grid, color: colors.text, fontSize: 12, borderRadius: 8 }}
                itemStyle={{ color: colors.text }}
                labelFormatter={(d: string) => k('weekOf', `Uke fra ${formatDate(d)}`, { '0': formatDate(d) })}
                formatter={(value: number | undefined) => [formatQty(value), k('picked', 'Plukket')]}
              />
              <Bar dataKey="quantity" fill={colors.series} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-gray-500">{k('noPicks', 'Ingen plukk de siste 13 ukene')}</p>
      )}
    </SectionCard>
  );
};
